import type { components } from '@connected/api-client/api.generated';

import { api } from '@connected/api-client';
import { buildUrl, generateRedirectUrl } from '@connected/api-client/lib/portal';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Button } from '@connected/ui/components/button';
import { Popout, PopoutBody, PopoutContent, PopoutHeader, PopoutTitle } from '@connected/ui/components/popout';
import { Skeleton } from '@connected/ui/components/skeleton';
import { Spinner } from '@connected/ui/components/spinner';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, CloudDownload, CloudUpload, Copy, MoveUpRight, Share2, X } from 'lucide-react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import z from 'zod';

import logo from '@/assets/logo-icon.png';
import { Breadcrumb } from '@/components/breadcrumb';
import { DataTable } from '@/components/data-table';
import { LoadingPage } from '@/components/loading-page';
import SearchInput from '@/components/search-input';
import { SubmenuNavigation } from '@/components/submenu-navigation';
import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';
import { showConfirm } from '@/stores/confirmer';

const tabKinds = {
    rapports: ['ElectricalProjectScope8Report', 'ElectricalProjectScope10Report', 'ElectricalProjectScope12Report', 'ElectricalProjectNen1010Report', 'ElectricalProjectNen3140Report'],
    inspections: ['ElectricalProjectInspectionPlan'],
    offers: ['ElectricalProjectPriceQuote'],
} as const satisfies Record<string, components['schemas']['DocumentContentKind'][]>;

export const Route = createFileRoute(
    '/_auth/dashboard/organizations/$organizationId/projects/$projectVersionId/documents/',
)({
    component: RouteComponent,
    validateSearch: z.object({
        tab: z.enum(Object.keys(tabKinds) as (keyof typeof tabKinds)[]).optional().default('rapports'),
    }),
});

type ListVersionQuery = components['schemas']['ListElectricalProjectDocumentQueryResultVersion'] & {
    version: string | null | number;
};
type ListDocumentQuery = components['schemas']['ListElectricalProjectDocumentQueryResultDocument'];
// type CreateDocumentQuery = components['schemas']['CreateElectricalProjectDocumentCommand'];

function ShareVersion({
    isOpen,
    setIsOpen,
    project,
    versionId,
    latestSharedId,
    projectId,
}: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    project: components['schemas']['ListElectricalProjectOrganizationQueryResult'];
    versionId: string;
    latestSharedId: string | null;
    projectId: string | null;
}) {
    const { t } = useTranslation('dashboard');
    const { projectVersionId } = Route.useParams();
    const [isPressed, setIsPressed] = useState<boolean>(false);

    const revokeShare = api.useMutation('put', '/api/v2/electrical/projects/revoke', {
        onError: (error) => {
            logger.error(
                {
                    system: 'RevokeElectricalProjectCommand',
                    error,
                },
                { apiHttpMethod: 'Put', apiUrl: '/api/v2/electrical/projects/revoke' },
            );
            toast.error(
                t(error?.Message ?? t('dashboard.general.messages.error')),
            );
        },
    });

    const { organizationId } = Route.useParams();

    const shareProject = api.useMutation('post', '/api/v2/electrical/projects/publish', {
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries(elektraAppQueryOptions.organisation.projects.documentListOptions({
                    electricalProjectVersionId: projectVersionId!,
                })),
                queryClient.invalidateQueries(portaalQueryOptions.electrical.project.projectObjectList({
                    organizationId,
                })),
            ]);
            toast.success(t('dashboard.projects.documents.message.shareSuccess'));
            setIsOpen(false);
        },
        onError: (error) => {
            logger.error(
                {
                    system: 'PublishElectricalProjectCommand',
                    error,
                },
                { apiHttpMethod: 'Post', apiUrl: '/api/v2/electrical/projects/publish' },
            );
            toast.error(
                t(error?.Message ?? t('dashboard.general.messages.error')),
            );
        },
        onSettled: () => {
            queryClient.invalidateQueries(portaalQueryOptions.electrical.project.view({
                ElectricalProjectId: projectId,
            }));
        },
    });

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <PopoutContent>
                <PopoutHeader>
                    <PopoutTitle><Share2 /></PopoutTitle>
                </PopoutHeader>
                <PopoutBody>
                    <div className="flex flex-col gap-3">
                        <div>
                            <p className="font-semibold text-lg">{t('dashboard.projects.documents.message.shareToPortal')}</p>
                            <p className="text-sm">{t('dashboard.projects.documents.message.shareDocumentMessage')}</p>
                        </div>
                        {project.Organizations?.map(org => (
                            <div className="rounded-xl bg-background-tertiary p-6 gap-4 flex flex-col" key={org.ElectricalProjectOrganizationId}>
                                <p className="text-md font-semibold">{org.Name}</p>
                                {org.ContactPersons?.map(person => (
                                    <div className="flex flex-row justify-between" key={person.ElectricalProjectContactPersonId}>
                                        <div className="flex flex-col">
                                            <span className="font-semibold text-sm">
                                                {
                                                    [person?.FirstName, person?.MiddleName, person?.LastName].filter(Boolean).join(' ')
                                                }
                                            </span>
                                            <span className="text-sm">
                                                {person.Email}
                                            </span>
                                        </div>
                                        <span className="text-sm">{person.Department}</span>
                                    </div>
                                ))}
                            </div>
                        ))}
                        <div className="w-full flex flex-row gap-2 mt-5">
                            <Button className="w-full" onClick={() => setIsOpen(false)} variant="outline">{t('dashboard.cancel')}</Button>
                            <Button
                                onClick={async () => {
                                    setIsPressed(!isPressed);

                                    if (latestSharedId) {
                                        await revokeShare.mutateAsync({
                                            body: {
                                                ElectricalProjectVersionId: latestSharedId,
                                            },
                                        }).then(() => {
                                            shareProject.mutateAsync(
                                                {
                                                    body: {
                                                        ElectricalProjectVersionId: versionId,
                                                    },
                                                },
                                            );
                                        });
                                    }
                                    if (!latestSharedId) {
                                        await shareProject.mutateAsync(
                                            {
                                                body: {
                                                    ElectricalProjectVersionId: versionId,
                                                },
                                            },
                                        );
                                    }
                                }}
                                type="submit"
                                variant="default"
                                className="w-full"
                                disabled={isPressed}
                            >
                                {t('dashboard.shareVersion')}
                            </Button>
                        </div>
                    </div>
                </PopoutBody>
            </PopoutContent>
        </Popout>
    );
}

async function DownloadFile(FileId?: string | null, FileName?: string | null) {
    try {
        const downloadOptions = portaalQueryOptions.electrical.documents.downloadBlob({
            DocumentId: FileId ?? '',
        });

        const response = await queryClient.ensureQueryData(
            downloadOptions,
        );

        const url = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = url;
        link.download = FileName || 'download';
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
    }
    // TODO: temp return error if file does exist in rows (tables in database) but the file itself is deleted from server
    catch {
        toast.error('Er is iets misgegaan, het bestand bestaat mogelijk niet op de server.');
    }
}

function ExpendedRow({
    data,
}: {
    data: ListDocumentQuery[];
}) {
    const { t } = useTranslation('dashboard');

    const [selectedDownload, setSelectedDownload] = useState({
        FileId: '',
        isDownloading: false,
    });

    const statusFile: Record<string, string> = {
        Unscheduled: t('dashboard.projects.documents.status.unscheduled'),
        Scheduled: t('dashboard.projects.documents.status.scheduled'),
        Completed: t('dashboard.projects.documents.status.completed'),
        Failed: t('dashboard.projects.documents.status.failed'),
    };

    const typeRapport: Record<string, string> = {
        ElectricalProjectScope8Report: t('dashboard.projects.documents.rapport.electricalProjectScope8Report'),
        ElectricalProjectScope10Report: t('dashboard.projects.documents.rapport.electricalProjectScope10Report'),
        ElectricalProjectScope12Report: t('dashboard.projects.documents.rapport.electricalProjectScope12Report'),
        ElectricalProjectNen1010Report: t('dashboard.projects.documents.rapport.electricalProjectNen1010Report'),
        ElectricalProjectNen3140Report: t('dashboard.projects.documents.rapport.electricalProjectNen3140Report'),
        ElectricalProjectInspectionPlan: t('dashboard.projects.documents.rapport.inspectionPlan'),
        ElectricalProjectPriceQuote: t('dashboard.projects.documents.rapport.priceQuote'),
        ElectricalProjectSevereDefectNotification: t('dashboard.projects.documents.rapport.electricalProjectSevereDefectNotification'),
    };

    const fileKind: Record<string, string> = {
        Pdf: 'pdf',
        Spreadsheet: 'xlsx',
        TextDocument: 'txt',
    };

    return (
        <div className="flex flex-row ml-9 gap-4">
            {data?.length > 0 && (
                <span className="h-16 w-px bg-border-primary">
                    <span className="mt-16 flex flex-col gap-14 border-l bg-border-primary">
                        {data.map(row => (
                            <span key={row.DocumentId} className="h-px w-10 bg-border-primary block" />
                        ))}
                    </span>
                </span>
            )}
            <DataTable
                whiteBgHeader
                columns={[
                    {
                        accessorKey: 'FileName',
                        header: () => <DataTable.Header>{t('dashboard.projects.documents.table.fileName')}</DataTable.Header>,
                        cell: ({ row }: { row: { original: ListDocumentQuery } }) => (
                            <DataTable.Cell variant="bold">
                                <div className="flex gap-3 items-center">
                                    {row?.original?.Status === 'Completed' && (
                                        <DataTable.Cell
                                            variant="fileIcon"
                                            fileType={row?.original?.FileKind != null
                                                ? fileKind[row.original.FileKind] ?? 'unknown'
                                                : 'unknown'}
                                        />
                                    )}
                                    <div>{row?.original?.FileName ?? t('dashboard.projects.documents.message.notCompleted')}</div>
                                </div>
                            </DataTable.Cell>
                        ),
                    },
                    {
                        accessorKey: 'typeRapport',
                        header: () => <DataTable.Header>{t('dashboard.requests.table.headers.typeRapport')}</DataTable.Header>,
                        cell: ({ row }: { row: { original: ListDocumentQuery } }) => (
                            <DataTable.Cell variant="default">
                                {row?.original?.Status != null
                                    ? (row.original.ContentKind ? typeRapport[row.original.ContentKind] ?? row.original.ContentKind : '')
                                    : row?.original?.ContentKind}
                            </DataTable.Cell>
                        ),
                    },
                    {
                        accessorKey: 'statusFile',
                        header: () => <DataTable.Header>{t('dashboard.requests.table.headers.status')}</DataTable.Header>,
                        cell: ({ row }: { row: { original: ListDocumentQuery } }) => (
                            <DataTable.Cell variant="default">
                                {row?.original?.Status != null ? statusFile[row.original.Status] : row?.original?.Status}
                                {row?.original?.Status === 'Scheduled' && <Spinner className="ml-1" />}
                            </DataTable.Cell>
                        ),
                    },
                    {
                        accessorKey: 'actions',
                        header: '',
                        cell: ({ row }: { row: { original: ListDocumentQuery } }) => (
                            <div className="flex gap-1 items-center justify-end ml-auto">
                                {row?.original?.Status === 'Completed' && (
                                    <Button
                                        variant="secondary"
                                        size="icon"
                                        className="flex gap-2 w-fit h-fit justify-start p-2 bg-background-primary text-foreground hover:bg-background-secondary"
                                        onClick={() => {
                                            setSelectedDownload({
                                                FileId: row?.original?.DocumentId ?? '',
                                                isDownloading: true,
                                            });
                                            DownloadFile(row?.original?.DocumentId, row?.original?.FileName).then(() => {
                                                setSelectedDownload({
                                                    FileId: '',
                                                    isDownloading: false,

                                                });
                                            });
                                        }}
                                    >

                                        {selectedDownload.FileId === row?.original?.DocumentId && selectedDownload.isDownloading
                                            ? <Spinner />
                                            : (
                                                    <CloudDownload className="text-text-primary/70 size-5" />
                                                )}

                                    </Button>
                                )}

                            </div>
                        ),
                    },
                ]}
                data={data}
                pageSize={100}
                className="border-none rounded-none shadow-none p-0 mb-3 w-full"
            />
        </div>
    );
}

function RouteComponent() {
    const { t } = useTranslation('dashboard');

    const { tab } = Route.useSearch();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [versionId, setVersionId] = useState<string | null>();

    const [isPlanned, setIsPlanned] = useState<boolean>(false);

    const { organizationId, projectVersionId } = Route.useParams();

    const [expandedId, setExpandedId] = useState<string | null>(null);

    const projectInfo = useQuery(
        portaalQueryOptions.electrical.project.objectView({
            ElectricalProjectVersionId: projectVersionId!,
        }),
    );
    const documentsList = useQuery({
        ...elektraAppQueryOptions.organisation.projects.documentListOptions({
            electricalProjectVersionId: projectVersionId!,
        }),
        refetchInterval: 5000,
    });

    const versionList = useQuery(
        elektraAppQueryOptions.project.request.organizationOptions({
            electricalProjectVersionId: projectVersionId!,
        }),
    );

    const projectsList = useQuery({
        ...portaalQueryOptions.electrical.project.projectObjectList({
            organizationId,
        }),
        refetchInterval: 5000,
    });

    const object = useMemo(() => {
        if (projectsList.isLoading || !projectsList.data) {
            return null;
        }
        if (projectsList?.isFetched && projectsList.isSuccess) {
            const project = projectsList?.data?.Projects?.find(
                p => p.CurrentElectricalProjectVersionId === projectVersionId,
            ) ?? null;

            if (!project) {
                return null;
            }
            return project.Object;
        }
        return null;
    }, [projectVersionId, projectsList.data, projectsList?.isFetched, projectsList.isLoading, projectsList.isSuccess]);

    const latestSharedProjectId = useMemo(() => {
        const project = projectsList?.data?.Projects?.find(row => row.CurrentElectricalProjectVersionId === projectVersionId);
        return project?.ElectricalProjectId;
    }, [projectVersionId, projectsList?.data?.Projects]);

    useEffect(() => {
        const project = projectsList?.data?.Projects?.find(row => row.CurrentElectricalProjectVersionId === projectVersionId);
        // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
        setIsPlanned(project?.Status === 'Request');
    }, [projectVersionId, projectsList?.data?.Projects]);

    const toggleExpand = (id: string | null) => {
        setExpandedId(prev => (prev === id ? null : id));
    };

    const revokeShare = api.useMutation('put', '/api/v2/electrical/projects/revoke', {
        onSuccess: async () => {
            Promise.all([
                queryClient.invalidateQueries(elektraAppQueryOptions.organisation.projects.documentListOptions({
                    electricalProjectVersionId: projectVersionId!,
                })),
                queryClient.invalidateQueries(portaalQueryOptions.electrical.project.projectObjectList({
                    organizationId,
                })),
            ]);
            toast.success(t('dashboard.projects.documents.message.revokeShare'));
            setIsOpen(false);
        },
        onError: (error) => {
            logger.error(
                {
                    system: 'RevokeElectricalProjectCommand',
                    error,
                },
                { apiHttpMethod: 'Put', apiUrl: '/api/v2/electrical/projects/revoke' },
            );
            toast.error(
                t(error?.Message ?? t('dashboard.general.messages.error')),
            );
        },
        onSettled: () => {
            queryClient.invalidateQueries(portaalQueryOptions.electrical.project.view({
                ElectricalProjectId: latestSharedProjectId ?? '',
            }));
        },
    });

    const typeInspection = useMemo<Record<string, string>>(() => ({
        RepeatInspection: t('dashboard.projects.table.columns.status.repeatinspection'),
        Inspection: t('dashboard.projects.table.columns.status.inpection'),
    }), [t]);

    const filteredVersions = useMemo(() => {
        if (documentsList.isError)
            return [];
        const tabKey = tab as keyof typeof tabKinds;
        const allowedKinds = (tabKinds[tabKey] ?? []) as components['schemas']['DocumentContentKind'][];

        const filtered = documentsList.data?.Versions?.filter(version =>
            version.Documents?.some(doc => doc.ContentKind != null && allowedKinds.includes(doc.ContentKind)),
        ) ?? [];

        const withVersions = filtered.map((row, index) => ({
            ...row,
            version: filtered.length - index,
        })) ?? [];

        // Voor het geval dat de laatste versie toch bovenaan getoond moet worden
        // const publishedIndex = withVersions.findIndex(row =>
        //     row.DocumentStatus === 'Published'
        //     && row.Documents?.some(
        //         doc => doc.ContentKind != null && allowedKinds.includes(doc.ContentKind),
        //     ),
        // );

        // if (publishedIndex > 0) {
        //     const [published] = withVersions.splice(publishedIndex, 1);
        //     withVersions.unshift(published);
        // }

        return withVersions;
    }, [documentsList.isError, documentsList.data?.Versions, tab]);

    const latestVersionId = useMemo(() => {
        const tabKey = tab as keyof typeof tabKinds;
        const allowedKinds = (tabKinds[tabKey] ?? []) as components['schemas']['DocumentContentKind'][];
        return filteredVersions?.find(row => row.DocumentStatus === 'Published' && row.Documents?.some(doc => doc.ContentKind != null && allowedKinds.includes(doc.ContentKind)))?.ElectricalProjectVersionId;
    }, [filteredVersions, tab]);

    useEffect(() => {
        // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
        setExpandedId(latestVersionId ?? '');
    }, [latestVersionId]);

    const portalProjectQuery = useQuery({
        ...portaalQueryOptions.electrical.project.view({
            ElectricalProjectId: latestSharedProjectId ?? '',
        }),
        enabled: !!latestSharedProjectId && !!versionList.isFetched,
    });

    const columns = useMemo(
        () => [
            {
                accessorKey: 'Name',
                header: () => (
                    <DataTable.Header>
                        {t('dashboard.projects.documents.table.name')}
                    </DataTable.Header>
                ),
                cell: ({ row }: { row: { original: ListVersionQuery } }) => {
                    const version = row.original;
                    const isExpanded = expandedId === version.ElectricalProjectVersionId;

                    return (
                        <Fragment>
                            <DataTable.Cell variant="bold">
                                <Button
                                    variant="ghost"
                                    onClick={() => toggleExpand(version.ElectricalProjectVersionId ?? null)}
                                >
                                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                </Button>
                                Versie
                                {' '}
                                {row?.original?.version}
                            </DataTable.Cell>
                        </Fragment>
                    );
                },
            },
            {
                accessorKey: 'Date',
                header: () => (
                    <DataTable.Header>
                        {t('dashboard.projects.documents.table.date')}
                    </DataTable.Header>
                ),
                cell: ({ row }: { row: { original: ListVersionQuery } }) => (
                    <DataTable.Cell variant="default">
                        {row?.original?.CreateDate && new Date(row.original.CreateDate).toLocaleDateString()}
                    </DataTable.Cell>
                ),
            },
            {
                accessorKey: 'Time',
                header: () => (
                    <DataTable.Header>
                        {t('dashboard.projects.documents.table.time')}
                    </DataTable.Header>
                ),
                cell: ({ row }: { row: { original: ListVersionQuery } }) => (
                    <DataTable.Cell variant="default">
                        {row?.original?.CreateDate && new Date(row.original.CreateDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </DataTable.Cell>
                ),
            },
            {
                accessorKey: 'TypeInspection',
                header: () => (
                    <DataTable.Header>
                        {t('dashboard.projects.documents.table.inspectionKind')}
                    </DataTable.Header>
                ),
                cell: ({ row }: { row: { original: ListVersionQuery } }) => (
                    <DataTable.Cell variant="default">
                        {row?.original?.InspectionKind ? typeInspection[row.original.InspectionKind] : row?.original?.InspectionKind}
                    </DataTable.Cell>
                ),
            },
            {
                id: 'actionModal',
                header: () => (
                    <DataTable.Header>
                        <div className="flex gap-1 items-center justify-end ml-auto mr-4">
                            {t('dashboard.projects.documents.table.actions')}
                        </div>
                    </DataTable.Header>
                ),
                cell: ({ row }: { row: { original: ListVersionQuery } }) => (
                    <DataTable.Cell>
                        <div className="flex gap-1 items-center justify-end ml-auto mr-4">
                            {row?.original?.DocumentStatus === 'Published' && row?.original?.ElectricalProjectVersionId
                                ? (
                                        <img
                                            src={logo}
                                            alt="logo"
                                            className="h-2.5 object-fill hover:cursor-pointer"
                                            onClick={async () => {
                                                if (await showConfirm(t('dashboard.projects.documents.message.revokeShareMessage'))) {
                                                    await revokeShare.mutateAsync({
                                                        body: {
                                                            ElectricalProjectVersionId: row?.original?.ElectricalProjectVersionId ?? '',
                                                        },
                                                    });
                                                }
                                            }}
                                        />
                                    )
                                : row?.original?.DocumentStatus === 'Unpublished'
                                    ? (
                                            <Share2
                                                onClick={() => {
                                                    setVersionId(row?.original?.ElectricalProjectVersionId);
                                                    setIsOpen(true);
                                                }}
                                                className="h-4 hover:cursor-pointer"
                                            />
                                        )
                                    : (
                                            <X
                                                className="h-4"
                                            />
                                        )}
                        </div>
                    </DataTable.Cell>
                ),
            },
        ],
        [expandedId, revokeShare, t, typeInspection],
    );

    const tableColumns = useMemo(
        () =>
            documentsList.isLoading
                ? columns.map(column => ({
                        ...column,
                        cell: () => <Skeleton className="h-8 w-full" />,
                    }))
                : columns,
        [columns, documentsList.isLoading],
    );

    const tableData = useMemo<ListVersionQuery[]>(() => {
        if (documentsList.isError) {
            toast.error(documentsList.error?.Message);
            return [];
        }
        // Reuse the already filtered versions for the table
        return filteredVersions;
    }, [documentsList.isError, documentsList.error?.Message, filteredVersions]);

    const obj = projectInfo?.data?.ProjectObject;
    const isFetched = projectInfo?.isFetched;
    const street = obj?.Street ?? '';
    const streetNumber = obj?.StreetNumber ?? '';
    const postalCode = obj?.PostalCode ?? '';
    const locality = obj?.Locality ?? '';
    const allEmpty = [street, streetNumber, postalCode, locality].every(v => !v.trim());

    if (projectInfo.isLoading || documentsList.isLoading || versionList.isLoading || projectsList.isLoading) {
        return <LoadingPage />;
    }

    return (
        <div>
            <Breadcrumb
                showHome={false}
                items={[
                    {
                        name: t('dashboard.projects.title'),
                        to: isPlanned ? '/dashboard/organizations/$organizationId/requests' : '/dashboard/organizations/$organizationId/projects',
                        params: { locationId: organizationId },
                    },
                    {
                        isActive: true,
                        name: t('dashboard.projects.documents.title'),
                    },
                ]}
            />
            <div className="flex flex-col max-sm:flex-wrap gap-8 justify-start">
                <div className="flex justify-between">
                    <div className="flex flex-col gap-1">
                        {(obj && !allEmpty) && (
                            <div>
                                <h3 className="text-2xl font-semibold">
                                    {isFetched && `${street} ${streetNumber}`}
                                </h3>
                                <p className="text-md text-text-primary/70">
                                    {isFetched && `${postalCode} ${locality}`}
                                </p>
                            </div>
                        )}
                        {(!obj || allEmpty) && (
                            <div>
                                <h3 className="text-2xl font-semibold">
                                    {t('dashboard.projects.documents.message.noObjectSet')}
                                </h3>
                                <p className="text-md text-text-primary/70 max-w-lg">
                                    {t('dashboard.projects.documents.message.noObjectSetSubtitle')}
                                </p>
                            </div>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Button
                            onClick={() => {
                                // navigator.clipboard.writeText(`${generateRedirectUrl(import.meta.env.VITE_DOMAIN_PORTAAL)}/customer${object?.ObjectId ? `/locations/${object?.ObjectId}/electrical/projects/${latestSharedProjectId}` : ''}`);
                                const redirectUrl = buildUrl(import.meta.env.VITE_DOMAIN_PORTAAL, `/customer/locations/${object?.ObjectId}/electrical/projects/${latestSharedProjectId}`);
                                if (redirectUrl) {
                                    navigator.clipboard.writeText(redirectUrl);
                                    toast.success(t('dashboard.projects.documents.message.copySuccess'));
                                }
                            }}
                            disabled={!portalProjectQuery.isSuccess}
                            variant="link"
                            className="flex items-center gap-1 border px-3 rounded-lg text-foreground hover:no-underline hover:bg-background max-sm:hidden"
                        >
                            <Copy className="w-5 h-5" />
                            {t('dashboard.projects.documents.message.copyToPortal')}
                        </Button>
                        <Button
                            disabled
                            variant="link"
                            className="flex items-center gap-1 border px-3 rounded-lg text-foreground hover:no-underline hover:bg-background max-sm:hidden"
                        >
                            <CloudUpload className="w-5 h-5" />
                            {t('dashboard.upload')}
                        </Button>
                        <a
                            href={generateRedirectUrl(
                                {
                                    redirectHost: import.meta.env.VITE_DOMAIN_PORTAAL,
                                    redirectPath: `/customer/locations/${object?.ObjectId}/electrical/projects/${latestSharedProjectId}`,
                                },
                            ) ?? ''}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <Button
                                variant="link"
                                disabled={!portalProjectQuery.isSuccess}
                                className="flex items-center gap-1 border px-3 rounded-lg text-foreground hover:no-underline hover:bg-background max-sm:hidden"
                            >
                                <MoveUpRight className="w-5 h-5" />
                                {t('dashboard.sidebar.customer')}
                            </Button>
                        </a>
                        <SearchInput route={Route} />
                    </div>
                </div>
                <div>
                    <div>
                        <SubmenuNavigation
                            items={[
                                {
                                    label: t('dashboard.projects.documents.submenu.rapports'),
                                    to: '/dashboard/organizations/$organizationId/projects/$projectVersionId/documents',
                                    search: { tab: 'rapports' },

                                },
                                {
                                    label: t('dashboard.projects.documents.submenu.inspectionPlans'),
                                    to: '/dashboard/organizations/$organizationId/projects/$projectVersionId/documents',
                                    search: { tab: 'inspections' },
                                },
                                {
                                    label: t('dashboard.projects.documents.submenu.offers'),
                                    to: '/dashboard/organizations/$organizationId/projects/$projectVersionId/documents',
                                    search: { tab: 'offers' },
                                },
                                {
                                    label: t('dashboard.projects.documents.submenu.statements'),

                                },
                            ]}
                        />
                    </div>
                    <DataTable
                        className="w-full"
                        columns={tableColumns}
                        data={tableData}
                        pageSize={100}
                        rowProps={row => ({
                            className: row.original.ElectricalProjectVersionId === latestVersionId ? 'bg-background-light-green/5' : '',
                        })}
                        whiteBgHeader
                        expandedRow={row =>
                            (expandedId === row.original.ElectricalProjectVersionId)
                                ? (
                                        <ExpendedRow data={row.original?.Documents ?? []} />
                                    )
                                : null}
                    />
                </div>
            </div>
            {
                isOpen && <ShareVersion isOpen={isOpen} projectId={latestSharedProjectId ?? ''} setIsOpen={setIsOpen} project={versionList.data ?? null!} versionId={versionId!} latestSharedId={latestVersionId ?? null} />
            }
        </div>
    );
}
